import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Trash2 } from 'lucide-react';

import { baseProductRowsFromOrders, formatDate, numberValue } from './selloutUtils';

export default function SellOutBaseProductPicker({
  selectedOrders,
  value,
  onChange,
}) {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => baseProductRowsFromOrders(selectedOrders), [selectedOrders]);
  const selectedKeys = useMemo(() => (value || []).map((row) => row.key), [value]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [
        row.order_code,
        row.product_code,
        row.product_description,
        row.format_name,
      ].some((field) => String(field || '').toLowerCase().includes(term))
    );
  }, [rows, search]);

  const byKey = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);

  const handleSelection = (model) => {
    const next = Array.from(model)
      .map((key) => {
        const previous = (value || []).find((row) => row.key === key);
        return previous || byKey.get(key);
      })
      .filter(Boolean);
    onChange(next);
  };

  const updateQuantity = (key, quantity) => {
    onChange((value || []).map((row) => (
      row.key === key ? { ...row, quantity_base: Math.max(numberValue(quantity), 0) } : row
    )));
  };

  const removeRow = (key) => {
    onChange((value || []).filter((row) => row.key !== key));
  };

  const columns = [
    { field: 'order_code', headerName: 'Orden', width: 130 },
    { field: 'product_code', headerName: 'Codigo', width: 130 },
    { field: 'product_description', headerName: 'Producto base', minWidth: 230, flex: 1 },
    { field: 'format_name', headerName: 'Formato', width: 130 },
    {
      field: 'quantity_base',
      headerName: 'Disponible',
      width: 120,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'order_date',
      headerName: 'Fecha',
      width: 115,
      valueGetter: (value) => formatDate(value),
    },
  ];

  return (
    <Stack spacing={1.25}>
      <TextField
        fullWidth
        size="small"
        label="Buscar producto base"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <Box sx={{ height: 315, width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.key}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedKeys}
          onRowSelectionModelChange={handleSelection}
          pageSizeOptions={[5, 10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          sx={{
            borderRadius: 1,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f7f8fa' },
          }}
        />
      </Box>

      {(value || []).length > 0 ? (
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell>Base</TableCell>
              <TableCell width={120} align="right">Cantidad</TableCell>
              <TableCell width={52} align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(value || []).map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <Typography variant="body2" noWrap title={`${row.order_code} - ${row.product_code} ${row.product_description}`}>
                    {row.order_code} - {row.product_code} {row.product_description}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={row.quantity_base}
                    onChange={(event) => updateQuantity(row.key, event.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Quitar">
                    <IconButton size="small" onClick={() => removeRow(row.key)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Stack>
  );
}
